import React, { useState, useCallback, useEffect } from 'react';
import { OrganizerProfile } from '../../../types/organizer.types';
import { PlaceAutocomplete, PlaceAutocompleteResult } from '../../../components/shared/PlaceAutocomplete';
import { supabaseOrganizerService } from '../../../supabase/organizer.service';
import { cloudinaryService } from '../../../services/cloudinary.service';
import { useAuth } from '../../../contexts/AuthContext';
import { useAnalytics } from '../../../contexts/AnalyticsContext';
import { AnalyticsService } from '../../../services/AnalyticsService';
import { supabase } from '../../../config/supabase';
import {
  ContentBlock,
  Button,
  Input,
  Textarea,
  FileInput,
  Icon
} from '../../../pages/DesignSystem';
import './ProfileEditForm.css';
import { useGoogleMaps } from '../../../contexts/GoogleMapsContext';
import { SecurityUtils } from '../../../utils/security';

interface ProfileEditFormProps {
  profile: OrganizerProfile | null;
  onClose: () => void;
  onSave: (updatedProfile: OrganizerProfile) => void;
  onFormDataChange?: (data: Partial<OrganizerProfile>) => void;
  mode?: 'page' | 'modal';
}

const ProfileEditForm: React.FC<ProfileEditFormProps> = ({
  profile,
  onClose,
  onSave,
  onFormDataChange,
  mode = 'page'
}) => {
  const { user } = useAuth();
  const analytics = useAnalytics();
  const [formData, setFormData] = useState<Partial<OrganizerProfile>>(profile || {});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSaving, setIsSaving] = useState(false);
  const { isLoaded, requestLoad } = useGoogleMaps();

  // Trigger le chargement de Google Maps quand le formulaire monte
  useEffect(() => {
    requestLoad();
  }, [requestLoad]);

  // Referral system states
  const [referrerEmail, setReferrerEmail] = useState(profile?.referrerEmail || '');
  const [referrerValid, setReferrerValid] = useState<boolean | null>(null);
  const [referrerName, setReferrerName] = useState('');
  const [checkingReferrer, setCheckingReferrer] = useState(false);
  const timeoutIdRef = React.useRef<NodeJS.Timeout | null>(null);

  // Notify parent of form data changes (for live preview in modal mode)
  useEffect(() => {
    if (onFormDataChange) {
      onFormDataChange(formData);
    }
  }, [formData, onFormDataChange]);

  // Gestion des champs texte et textarea
  const handleInputChange = (name: string) => (value: string | React.ChangeEvent<HTMLInputElement>) => {
    const newValue = typeof value === 'string' ? value : value.target.value;
    setFormData(prev => ({ ...prev, [name]: newValue }));
    
    // Effacer l'erreur quand l'utilisateur modifie le champ
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  // Ajouter cette fonction après handleInputChange
  const handleWebsiteChange = (value: string | React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = typeof value === 'string' ? value : value.target.value;

    // Si le champ est vide ou contient uniquement "https://", gardez "https://"
    if (!inputValue || inputValue === 'https://') {
      setFormData(prev => ({ ...prev, website: 'https://' }));
    }
    // Si l'utilisateur a saisi quelque chose sans le préfixe, ajoutez-le
    else if (!inputValue.startsWith('http://') && !inputValue.startsWith('https://')) {
      setFormData(prev => ({ ...prev, website: `https://${inputValue}` }));
    }
    // Sinon, utilisez la valeur telle quelle
    else {
      setFormData(prev => ({ ...prev, website: inputValue }));
    }

    // Effacer l'erreur
    if (errors.website) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.website;
        return newErrors;
      });
    }
  };

  // Debounced function to check referrer validity via secure RPC
  const checkReferrer = useCallback(
    (email: string) => {
      if (timeoutIdRef.current) {
        clearTimeout(timeoutIdRef.current);
      }

      if (!email || !email.includes('@')) {
        setReferrerValid(null);
        setReferrerName('');
        setCheckingReferrer(false);
        return;
      }

      setCheckingReferrer(true);

      timeoutIdRef.current = setTimeout(async () => {
        try {
          // Use secure RPC to validate referrer without exposing sensitive data
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const { data, error } = await (supabase.rpc as any)('validate_referrer_code', {
            referrer_email: email
          });

          if (error) {
            console.error('Error checking referrer:', error);
            setReferrerValid(false);
            setReferrerName('Error validating referrer');
            return;
          }

          const result = Array.isArray(data) ? data[0] : data;

          if (!result) {
            setReferrerValid(false);
            setReferrerName('Error validating referrer');
          } else if (result.is_valid) {
            setReferrerValid(true);
            setReferrerName(result.organizer_name || email);
          } else {
            setReferrerValid(false);
            // Map error reasons to user-friendly messages
            switch (result.error_reason) {
              case 'not_found':
                setReferrerName('User not found');
                break;
              case 'self_referral':
                setReferrerName('You cannot refer yourself');
                break;
              case 'not_paid_subscriber':
                setReferrerName('This user must have an active paid subscription');
                break;
              default:
                setReferrerName('Invalid referrer email');
            }
          }
        } catch (err) {
          console.error('Error checking referrer:', err);
          setReferrerValid(false);
          setReferrerName('Error validating referrer');
        } finally {
          setCheckingReferrer(false);
        }
      }, 500);
    },
    []
  );

  // Handle referrer email change
  const handleReferrerEmailChange = (value: string) => {
    setReferrerEmail(value);
    checkReferrer(value);
  };

  // Handle place selection from PlaceAutocomplete
  const handlePlaceSelect = useCallback((result: PlaceAutocompleteResult) => {
    setFormData(prev => {
      const updates: Partial<OrganizerProfile> = {};

      // Stocker l'adresse complète dans location
      if (result.formattedAddress) {
        updates.location = result.formattedAddress;
      }

      // Stocker le nom du lieu dans locationName (displayName au lieu de name)
      if (result.displayName) {
        updates.locationName = result.displayName;
      }

      // Stocker les coordonnées
      if (result.location) {
        updates.latitude = result.location.lat;
        updates.longitude = result.location.lng;
      }

      return { ...prev, ...updates };
    });
  }, []);

  // Traitement de l'image d'avatar
  const handleAvatarUpload = (files: FileList | null) => {
    const file = files?.[0];
    if (file) {
      // Créer une URL temporaire pour la prévisualisation
      const tempUrl = URL.createObjectURL(file);
      setFormData(prev => ({ ...prev, organizerImage: tempUrl, avatarUploading: true }));
      
      // Upload vers Cloudinary
      cloudinaryService.uploadImage(file)
        .then(cloudinaryUrl => {
          if (cloudinaryUrl) {
            setFormData(prev => ({ 
              ...prev, 
              organizerImage: cloudinaryUrl, 
              avatarUploading: false 
            }));
            
            // Tracker le succès de l'upload
            analytics.trackImageUpload(
              'avatar',
              file.size,
              file.type.split('/')[1] || 'unknown',
              true
            );
          } else {
            console.error('No URL received from Cloudinary for avatar');
            setFormData(prev => ({ ...prev, avatarUploading: false }));
            
            // Tracker l'échec de l'upload
            analytics.trackImageUpload(
              'avatar',
              file.size,
              file.type.split('/')[1] || 'unknown',
              false
            );
          }
          URL.revokeObjectURL(tempUrl);
        })
        .catch(error => {
          console.error('Error uploading avatar:', error);
          setFormData(prev => ({ 
            ...prev, 
            avatarUploading: false 
          }));
          
          // Tracker l'échec de l'upload
          analytics.trackImageUpload(
            'avatar',
            file.size,
            file.type.split('/')[1] || 'unknown',
            false
          );
          
          URL.revokeObjectURL(tempUrl);
        });
    }
  };

  // Traitement des images de galerie
  const handleGalleryImageUpload = (files: FileList | null) => {
    if (!files || files.length === 0) return;

    const file = files[0]; // On ne prend que le premier fichier
    
    // Créer une URL temporaire pour la prévisualisation
    const tempUrl = URL.createObjectURL(file);
    
    // IMPORTANT: Eviter les mutations directes, utiliser un nouveau tableau
    setFormData(prev => {
      const newGalleryImages = [...(prev.galleryImages || []), tempUrl];
      return { 
        ...prev, 
        galleryImages: newGalleryImages, 
        galleryUploading: true 
      };
    });
    
    // Upload vers Cloudinary
    cloudinaryService.uploadImage(file)
      .then(cloudinaryUrl => {
        if (cloudinaryUrl) {
          // Utiliser setState avec callback pour avoir la dernière valeur
          setFormData(prev => {
            // IMPORTANT: Créer un nouveau tableau pour éviter les mutations
            const updatedImages = (prev.galleryImages || []).map(img => 
              img === tempUrl ? cloudinaryUrl : img
            );
            
            const totalImages = updatedImages.length;
            
            // Tracker le succès de l'upload
            analytics.trackImageUpload(
              'galerie',
              file.size,
              file.type.split('/')[1] || 'unknown',
              true,
              totalImages
            );
            
            return { 
              ...prev, 
              galleryImages: updatedImages, 
              galleryUploading: false 
            };
          });
        } else {
          console.error('No URL received from Cloudinary for gallery image');
          setFormData(prev => {
            const filteredImages = (prev.galleryImages || []).filter(img => img !== tempUrl);
            
            // Tracker l'échec de l'upload
            analytics.trackImageUpload(
              'galerie',
              file.size,
              file.type.split('/')[1] || 'unknown',
              false,
              filteredImages.length
            );
            
            return { 
              ...prev, 
              galleryImages: filteredImages,
              galleryUploading: false 
            };
          });
        }
        URL.revokeObjectURL(tempUrl);
      })
      .catch(error => {
        console.error('Error uploading gallery image:', error);
        setFormData(prev => {
          const filteredImages = (prev.galleryImages || []).filter(img => img !== tempUrl);
          
          // Tracker l'échec de l'upload
          analytics.trackImageUpload(
            'galerie',
            file.size,
            file.type.split('/')[1] || 'unknown',
            false,
            filteredImages.length
          );
          
          return { 
            ...prev, 
            galleryImages: filteredImages,
            galleryUploading: false 
          };
        });
        URL.revokeObjectURL(tempUrl);
      });
  };

  // Gérer la suppression d'une image de la galerie
  const handleRemoveGalleryImage = (imageUrl: string) => {
    const updatedGalleryImages = (formData.galleryImages || []).filter(img => img !== imageUrl);
    setFormData(prev => ({ ...prev, galleryImages: updatedGalleryImages }));
  };

  // Validation du formulaire avant soumission
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    
    // Validation des champs obligatoires
    if (!formData.organizer?.trim()) {
      newErrors.organizer = 'Organizer name is required';
    }
    
    if (!formData.location?.trim()) {
      newErrors.location = 'Location is required';
    }
    
    // Validation du format email
    if (formData.email && !/^\S+@\S+\.\S+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    // Validation du format URL
    if (formData.website && !/^https?:\/\/\S+\.\S+/.test(formData.website)) {
      newErrors.website = 'Please enter a valid URL starting with http:// or https://';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Gérer la soumission du formulaire
  const handleSubmit = async () => {
    if (!validateForm()) {
      setErrors(prev => ({ ...prev, submit: 'Please correct the errors above' }));
      return;
    }
    
    setIsSaving(true);

    try {
      // 🔒 SANITISATION DES DONNÉES AVANT SAUVEGARDE
      const sanitizedFormData = {
        ...formData,
        organizer: SecurityUtils.sanitizeInputForSubmission(formData.organizer || ''),
        subtitle: SecurityUtils.sanitizeInputForSubmission(formData.subtitle || ''),
        description: SecurityUtils.sanitizeInputForSubmission(formData.description || ''),
        location: SecurityUtils.sanitizeInputForSubmission(formData.location || ''),
        locationName: SecurityUtils.sanitizeInputForSubmission(formData.locationName || ''),
        email: SecurityUtils.sanitizeInputForSubmission(formData.email || ''),
        phone: SecurityUtils.sanitizeInputForSubmission(formData.phone || ''),
        website: formData.website ? SecurityUtils.sanitizeInputForSubmission(formData.website) : '',
        organizerContact: SecurityUtils.sanitizeInputForSubmission(formData.organizerContact || ''),
        referrerEmail: referrerEmail && referrerValid ? SecurityUtils.sanitizeInputForSubmission(referrerEmail) : '',
        // Les images et coordonnées ne nécessitent pas de sanitisation
        organizerImage: formData.organizerImage,
        galleryImages: formData.galleryImages,
        latitude: formData.latitude,
        longitude: formData.longitude
      };

      // Afficher les données avant la sauvegarde
      // Sauvegarde du profil avec données sanitisées
      
      const updatedProfile = await supabaseOrganizerService.updateProfile(user?.uid || '', sanitizedFormData);
      
      // Calculer et tracker le score de complétion après sauvegarde
      const completion = AnalyticsService.calculateProfileCompletionScore(updatedProfile);
      analytics.trackProfileCompletionScore(
        completion.elementsCompletes,
        completion.elementsManquants,
        completion.score
      );
      
      onSave(updatedProfile);
      // In page mode, close after save. In modal mode, let the modal's onSave callback handle closing
      if (mode === 'page') {
        onClose();
      }
    } catch (error) {
      console.error('Error saving profile:', error);
      setErrors({ submit: 'An error occurred while saving your profile. Please contact us if the problem persists.' });
    } finally {
      setIsSaving(false);
    }
  };

  // Gérer le bouton Annuler
  const handleCancel = () => {
    onClose();
  };

  // Ajoutez ce code de débogage juste après les déclarations d'état
  // Google Maps debug logs removed for performance

  return (
    <div className={`profile-edit ${mode === 'modal' ? 'profile-edit--modal' : ''}`}>
      {mode === 'page' && (
        <div className="profile-edit__header">
          <h1 className="profile-edit__title">Edit Your Profile</h1>
          <p className="profile-edit__subtitle">
            Update your information to make it easier for people to discover and connect with you
          </p>
        </div>
      )}

      <div className="profile-edit__form" >
        {/* Basic Information Section */}
        <ContentBlock title="Basic Information" spacing="xs" border="light">
          <div className="profile-edit__group">
            <label className="profile-edit__label">
              Name *
              <span className="profile-edit__label-hint">
                (Your name or organization name)
              </span>
            </label>
            <Input
              id="organizer-name"
              name="organizer"
              type="text"
              value={formData.organizer || ''}
              onChange={handleInputChange('organizer')}
              error={errors.organizer}
              placeholder="Your organization name"
              icon="User"
              required
            />
          </div>

          <div className="profile-edit__group">
            <label className="profile-edit__label">
              Tagline
              <span className="profile-edit__label-hint">
                (A short bio like your Instagram profile - show your vibe!)
              </span>
            </label>
            <Input
              id="organizer-subtitle"
              name="subtitle"
              type="text"
              value={formData.subtitle || ''}
              onChange={handleInputChange('subtitle')}
              error={errors.subtitle}
              placeholder="e.g. 'The best Latin parties in Paris'"
              icon="Type"
              helper="💡 Tip: Keep it short and catchy, around 60 characters"
            />
          </div>

          <div className="profile-edit__group">
            <label className="profile-edit__label">
              Description
              <span className="profile-edit__label-hint">
                (Tell your story and what makes you unique! ✨)
              </span>
            </label>
            <Textarea
              id="organizer-description"
              name="description"
              value={formData.description || ''}
              onChange={handleInputChange('description')}
              error={errors.description}
              placeholder="Share what you're all about - your mission, vibe, history, or what makes your space special..."
              rows={5}
              helper="💡 Pro tip: A great description helps connect with potential attendees before they discover your events!"
            />
          </div>

          <div className="profile-edit__group">
            <label className="profile-edit__label">
              Location *
              <span className="profile-edit__label-hint">
                (Your home base - we'll use this as default for your events! 📍)
              </span>
            </label>
            {isLoaded ? (
              <PlaceAutocomplete
                placeholder="Search for your location"
                value={formData.location || ''}
                onChange={(value) => setFormData(prev => ({ ...prev, location: value }))}
                onPlaceSelect={handlePlaceSelect}
                error={errors.location}
                icon="MapPin"
                className="profile-edit__input"
              />
            ) : (
              <Input
                id="organizer-location"
                name="location"
                type="text"
                value={formData.location || ''}
                onChange={handleInputChange('location')}
                error={errors.location}
                placeholder="Enter your location"
                icon="MapPin"
                required
              />
            )}
          </div>
        </ContentBlock>

        {/* Contact Information Section */}
        <ContentBlock title="Contact Information" spacing="xs" border="light">
          <div className="profile-edit__group">
            <label className="profile-edit__label">
              Email
              <span className="profile-edit__label-hint">
                (Your business email for inquiries)
              </span>
            </label>
            <Input
              id="organizer-email"
              name="email"
              type="email"
              value={formData.email || ''}
              onChange={handleInputChange('email')}
              error={errors.email}
              placeholder="contact@yourorganization.com"
              icon="Mail"
            />
          </div>

          <div className="profile-edit__group">
            <label className="profile-edit__label">
              Phone Number
              <span className="profile-edit__label-hint">
                (Optional contact number for attendees)
              </span>
            </label>
            <Input
              id="organizer-phone"
              name="phone"
              type="tel"
              value={formData.phone || ''}
              onChange={handleInputChange('phone')}
              error={errors.phone}
              placeholder="+33 6 12 34 56 78"
              icon="Phone"
            />
          </div>

          <div className="profile-edit__group">
            <label className="profile-edit__label">
              Website
              <span className="profile-edit__label-hint">
                (Your personal or organization website)
              </span>
            </label>
            <Input
              id="organizer-website"
              name="website"
              type="url"
              value={formData.website || 'https://'}
              onChange={handleWebsiteChange}
              error={errors.website}
              placeholder="https://www.yourwebsite.com"
              icon="Globe"
              helper="💡 Tip: Start typing and we'll add https:// for you"
            />
          </div>

          <div className="profile-edit__group">
            <label className="profile-edit__label">
              Instagram Username
              <span className="profile-edit__label-hint">
                (Without the @ symbol)
              </span>
            </label>
            <Input
              id="organizer-instagram"
              name="instagram"
              type="text"
              value={formData.instagram || ''}
              onChange={handleInputChange('instagram')}
              error={errors.instagram}
              placeholder="your instagram"
              icon="Instagram"
              helper="💡 Tip: Just enter your username, not the full URL"
            />
          </div>
        </ContentBlock>

        {/* Referral Section */}
        <ContentBlock title="Referral (Optional)" spacing="xs" border="light">
          <div className="profile-edit__group">
            <label className="profile-edit__label">
              Referred by
              <span className="profile-edit__label-hint">
                (Who referred you to What 2 Do? Get 1 month free!)
              </span>
            </label>
            <Input
              id="referrer-email"
              name="referrerEmail"
              type="email"
              value={referrerEmail}
              onChange={handleReferrerEmailChange}
              placeholder="friend@example.com"
              icon="Users"
              helper="💡 Enter the email of the organizer who referred you. You get 1 month free, they get 1 month free when you become a paying customer!"
              disabled={isSaving}
            />

            {/* Checking state */}
            {checkingReferrer && (
              <div className="profile-edit__referral-status profile-edit__referral-status--checking">
                <Icon name="Loader" size={14} />
                <span>Checking...</span>
              </div>
            )}

            {/* Success state */}
            {referrerValid === true && !checkingReferrer && (
              <div className="profile-edit__referral-status profile-edit__referral-status--success">
                <Icon name="Check" size={16} />
                <span>You'll get 1 month free thanks to {referrerName} !</span>
              </div>
            )}

            {/* Error state */}
            {referrerValid === false && referrerEmail && !checkingReferrer && (
              <div className="profile-edit__referral-status profile-edit__referral-status--error">
                <Icon name="AlertCircle" size={16} />
                <span>{referrerName || 'Invalid referrer email'}</span>
              </div>
            )}
          </div>
        </ContentBlock>

        {/* Profile Images Section */}
        <ContentBlock title="Profile Images" spacing="xs" border="light">
          <div className="profile-edit__group">
            <label className="profile-edit__label">
              Profile Picture
              <span className="profile-edit__label-hint">
                (This will be displayed as your avatar)
              </span>
            </label>
            <FileInput
              id="organizer-avatar"
              name="avatar"
              accept="image/*"
              onChange={handleAvatarUpload}
              helper="💡 Tip: Use a clear, recognizable image. A square image works best. Maximum 5MB."
              dragAndDrop
            />
            {formData.organizerImage && (
              <div className="profile-edit__avatar-preview">
                <img src={formData.organizerImage} alt="Avatar Preview" />
                {formData.avatarUploading && <div className="profile-edit__upload-overlay">Uploading...</div>}
              </div>
            )}
          </div>

          <div className="profile-edit__group">
            <label className="profile-edit__label">
              Gallery Images
              <span className="profile-edit__label-hint">
                (Showcase your events or space with beautiful images)
              </span>
            </label>
            <FileInput
              id="organizer-gallery"
              name="gallery"
              accept="image/*"
              onChange={handleGalleryImageUpload}
              helper="💡 Tip: Add up to 5 high-quality images that represent your events. Landscape orientation works best."
              dragAndDrop
              disabled={(formData.galleryImages?.length || 0) >= 5 || formData.galleryUploading}
            />
            
            {/* Prévisualisation des images SÉPARÉE */}
            {formData.galleryImages && formData.galleryImages.length > 0 && (
              <div className="profile-edit__gallery-preview">
                {formData.galleryImages.map((image, index) => (
                  <div key={`gallery-${index}-${image.substring(0, 8)}`} className="profile-edit__gallery-item">
                    <img src={image} alt={`Gallery ${index + 1}`} />
                    <button 
                      type="button"
                      className="profile-edit__gallery-remove"
                      onClick={() => handleRemoveGalleryImage(image)}
                      aria-label="Remove image"
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            )}
            {formData.galleryUploading && (
              <div className="profile-edit__gallery-upload-indicator">
                Uploading image... Please wait
              </div>
            )}
          </div>
        </ContentBlock>

        {/* Message d'erreur global */}
        {errors.submit && (
          <div className="profile-edit__error-message">
            ⚠️ {errors.submit}
          </div>
        )}

        {/* Action Buttons */}
        <div className={`profile-edit__actions ${mode === 'modal' ? 'profile-edit__actions--modal' : ''}`}>
          <Button
            variant="secondary"
            size="md"
            onClick={handleCancel}
            disabled={isSaving}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            size="md"
            onClick={handleSubmit}
            disabled={isSaving}
          >
            {isSaving ? 'Saving...' : 'Save Profile'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ProfileEditForm; 